import user from 'js/lib/user';

import type { CourseData } from 'bundles/course-v2/components/Course';
import type OpenCourseMembershipsV1 from 'bundles/naptimejs/resources/openCourseMemberships.v1';

/**
 * This function is used to get the relative URL for the course (aka /learn/{slug}/{relativePath}).
 * @param slug - The slug of the course.
 * @param relativePath - The relative path to the course.
 * @returns The relative URL for the course.
 */
export function getRelativeUrl(slug: string, relativePath: string): string {
  return `/learn/${slug}/${relativePath}`;
}

/**
 * This function is used to get the welcome URL for the course.
 * @param slug - The slug of the course.
 * @returns The relative welcome URL for the course.
 */
export function getWelcomeUrl(slug: string): string {
  return getRelativeUrl(slug, 'home/welcome');
}

/**
 * This function is used to get the grades URL for the course.
 * @param slug - The slug of the course.
 * @returns The relative grades URL for the course.
 */
export function getGradesUrl(slug: string): string {
  return getRelativeUrl(slug, 'home/assignments');
}

/**
 * This function is used to get the notes URL for the course.
 * @param slug - The slug of the course.
 * @returns The relative notes URL for the course.
 */
export function getNotesUrl(slug: string): string {
  return getRelativeUrl(slug, 'home/notes');
}

/**
 * This function is used to get the week URL for the course.
 * @param slug - The slug of the course.
 * @param week - The week number.
 * @param isWeekToModuleEnabled - Whether the week to module is enabled.
 * @returns The relative week URL for the course.
 */
export function getWeekUrl(slug: string, week: number, isWeekToModuleEnabled: boolean): string {
  if (isWeekToModuleEnabled) {
    return getRelativeUrl(slug, `home/module/${week}`);
  } else {
    return getRelativeUrl(slug, `home/week/${week}`);
  }
}

/**
 * This function is used to get the URL for a particular extra resource (like a downloadable asset or link) in the course.
 * @param slug - The slug of the course.
 * @param id - The id of the resource.
 * @returns The relative URL for the resource.
 */
export function getReferenceUrl(slug: string, id: string): string {
  return getRelativeUrl(slug, `resources/${id}`);
}

/**
 * This function is used to check if the user can access the course based on if a course is found and if the user has a valid role.
 * @param openCourseMemberships - The open course membership data for the course.
 * @param course - The course.
 * @returns Whether the user can access the course.
 */
export function canAccessCourse(openCourseMemberships: OpenCourseMembershipsV1[], course?: CourseData | null): boolean {
  const courseRoleBlacklist = ['BROWSER', 'NOT_ENROLLED', 'PRE_ENROLLED_LEARNER'];
  const courseRole = !!openCourseMemberships[0] && openCourseMemberships[0].courseRole;

  // checking course existence this way invalidates use of old slugs
  // openCourseMemberships doesn't
  const courseFound = !!course;
  const validRole = user.isSuperuser() || (courseRole && !courseRoleBlacklist.includes(courseRole));

  return courseFound && validRole;
}
